import React, { useState } from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { useCandidate } from './CandidateProvider'
import { cn } from '~/lib/utils'
import EditCandidateDialog from './EditCandidateDialog'
import { useFormatTime } from '~/hooks/useFormatTime'

const BasicsSection = () => {
  const [expanded, setExpanded] = useState(false)
  const { candidate } = useCandidate()
  const {
    email,
    companyName,
    product,
    programLevel,
    programDuration,
    coachFullName,
    currentUserIsCoach,
  } = candidate || {}
  const coachClassName = currentUserIsCoach
    ? 'text-intoo-green-coachcta'
    : 'text-red-800'
  const productText =
    product && programLevel && programDuration
      ? `${product} (${programLevel}-${programDuration})`
      : ''
  const coachFullNameDisplay = coachFullName || 'Not Assigned'

  return (
    <div>
      <Table className="w-full" tableClass="rounded-2xl border border-silver">
        <TableHeader className="h-6 bg-intoo-blue-medium">
          <TableRow className="hover:bg-intoo-blue-medium">
            <TableHead colSpan={4} className="text-lg text-white">
              Profile Basic Information
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <StripedRow>
            <LeftCells label="Email">{email}</LeftCells>
            <RightCells label="Company">{companyName}</RightCells>
          </StripedRow>
          <StripedRow>
            <LeftCells label="Product">{productText}</LeftCells>
            <RightCells label="Coach" valueCellClassName={coachClassName}>
              {candidate && coachFullNameDisplay}
            </RightCells>
          </StripedRow>
          {expanded && <DetailRows />}
        </TableBody>
      </Table>
      <div className="flex flex-row justify-end">
        <button
          className="mt-4 text-intoo-blue-medium underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>
      <div className="flex flex-row justify-end">
        <EditCandidateDialog candidate={candidate} />
      </div>
    </div>
  )
}
export default BasicsSection

const DetailRows = () => {
  const { candidate } = useCandidate()
  const {
    division,
    invitationCode,
    activatedAt,
    lastLogin,
    phone,
    resumeReviewPhone,
    multipleInvitations,
    countryLanguage,
    countryFlag,
    platformAccess,
    opNextGen,
    coachInstructions,
    coachingBriefPath,
    activityPoints,
    expiresAt,
    phoneType,
    address,
    resumeSeniorityLevel,
    isTestCandidate,
    personalizedDashboard,
    employeeLevel,
    managerFullName,
    managerEmail,
    managerPhone,
    supportUntilLanding,
  } = candidate || {}

  const formatTime = useFormatTime()

  const displayActivatedAt = activatedAt
    ? formatTime(activatedAt, 'short')
    : 'Never'
  const displayExpiresAt = expiresAt ? formatTime(expiresAt, 'short') : 'Never'
  const displayLastLogin = lastLogin ? formatTime(lastLogin) : 'Never'

  return (
    <>
      <StripedRow>
        <LeftCells label="Division">{division}</LeftCells>
        <RightCells label="Coach Instrutions">
          {coachInstructions}
          {coachingBriefPath && (
            <a href={coachingBriefPath} target="_blank" rel="noreferrer">
              Coaching Brief
            </a>
          )}
        </RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Invitation">{invitationCode}</LeftCells>
        <RightCells label="Activity Points">{activityPoints}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Activated On">{displayActivatedAt}</LeftCells>
        <RightCells label="Expires On">
          {displayExpiresAt}{' '}
          {supportUntilLanding ? (
            <span className="landing-support-flag">Support Until Landing</span>
          ) : (
            ''
          )}
        </RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Last Login">{displayLastLogin}</LeftCells>
        <RightCells label="Phone Type">{phoneType}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Phone">{phone}</LeftCells>
        <RightCells label="Address">{address}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Resume Review Phone Number">
          {resumeReviewPhone}
        </LeftCells>
        <RightCells label="Resume Seniority Level">
          {resumeSeniorityLevel}
        </RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Multiple Invitations">
          {multipleInvitations ? 'Yes' : 'No'}
        </LeftCells>
        <RightCells label="Test">{isTestCandidate ? 'Yes' : 'No'}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Country / Language">
          {countryLanguage}{' '}
          <i className={`glyphicon bfh-flag-${countryFlag}`}></i>
        </LeftCells>
        <RightCells label="Personalized Dashboard">
          {personalizedDashboard ? 'Yes' : 'No'}
        </RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Platform Access">
          {platformAccess ? 'Yes' : 'No'}
        </LeftCells>
        <RightCells label="Employee Level">{employeeLevel}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="OP Next-Gen Enabled">
          {opNextGen ? 'Yes' : 'No'}
        </LeftCells>
        <RightCells label="Manager">{managerFullName}</RightCells>
      </StripedRow>
      <StripedRow>
        <LeftCells label="Manager Email">{managerEmail}</LeftCells>
        <RightCells label="Manager Phone">{managerPhone}</RightCells>
      </StripedRow>
    </>
  )
}

const RightCells = ({ label, valueCellClassName, children }) => {
  return (
    <>
      <TableCell className="border-x border-silver font-semibold">
        {label}
      </TableCell>
      <TableCell className={valueCellClassName}>{children}</TableCell>
    </>
  )
}

const LeftCells = ({ label, valueCellClassName, children }) => {
  return (
    <>
      <TableCell className="border-r border-silver font-semibold">
        {label}
      </TableCell>
      <TableCell className={valueCellClassName}>{children}</TableCell>
    </>
  )
}

const StripedRow = ({ children, className, ...props }) => {
  return (
    <TableRow
      {...props}
      className={cn(
        'odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}
